import React, { useState } from "react";
import { Forward, X } from "lucide-react";
import { toast } from "react-toastify";
import { crmAPI } from "../../../services/CreditManagerPortal/crmAPI";
import { creditAPI } from "../../../services/CreditManagerPortal/creditApi";

const BankCard = ({
  openConfirmDialog, // called with distribution._id
  distribution,
  isSubmitting,
  application = {},
  onDetailsSentSuccessfully, // callback to parent to refresh
  allDistributions = [], // all distributions to check for duplicates
}) => {
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [isSendingForApproval, setIsSendingForApproval] = useState(false);

  if (!distribution) return null;

  // Check if there's an approved distribution with same bank details
  const hasSameBankApproved = allDistributions.some(dist => {
    if (dist._id === distribution._id) return false; // Don't compare with itself
    if (dist.status !== "approved") return false; // Only check approved distributions
    
    const currentBank = distribution.bankingPartner || {};
    const otherBank = dist.bankingPartner || {};
    
    return (
      currentBank.bankCategory === otherBank.bankCategory &&
      currentBank.branchName === otherBank.branchName &&
      currentBank.bankAddress === otherBank.bankAddress
    );
  });



  const handleRejectionClick = () => {
    setSelectedReason(distribution.rejectionReason || "No rejection reason provided.");
    setShowReasonModal(true);
  };

  const handleCloseModal = () => {
    setShowReasonModal(false);
    setSelectedReason("");
  };

  const handleSendForApproval = async () => {
    if (!application?._id || !distribution?._id) {
      toast.error("Application or Distribution ID not available.");
      return;
    }

    setIsSendingForApproval(true);
    try {
      // ✅ UPDATE EXISTING DISTRIBUTION (not create new)
      await crmAPI.distributeLoanAndUpdateAmount(application._id, {
        partnerDistributions: [{
          partnerId: distribution.bankingPartner?._id,
          note: "Application sent for bank approval again",
          bankType: "external",
          existingDistributionId: distribution._id, // ← Backend uses this to UPDATE
          updateStatus: "processing", // ← Reset to processing
          clearRejectionReason: true // ← Clear rejection reason
        }],
        amountType: "requested",
        representativeIds: [],
        isReprocessing: true // ← Tells backend this is re-approval
      });

      toast.success("Application sent for bank approval successfully!");
      handleCloseModal();
      if (typeof onDetailsSentSuccessfully === "function") {
        onDetailsSentSuccessfully();
      }
    } catch (error) {
      console.error("Error sending for approval:", error);
      toast.error(error.message || "Failed to send for bank approval.");
    } finally {
      setIsSendingForApproval(false);
    }
  };

  const partnerDetails = distribution.bankingPartner || {};
  const bankName = (partnerDetails.bankName || "Unknown Partner").trim();
  const bankLogo = partnerDetails.logo;
  const bankLogoChar = bankName.charAt(0).toUpperCase() || "P";
  const applicationId = application?._id;

  // Data sent when "Send loan details to customers" is clicked
  const dataToCustomer = {
    bankName,
    approvedAmount:
      distribution.approveAmount ?? application.approvedAmount ?? application.principle,
    monthlyEMI: distribution.emi ?? application.monthlyEMI ?? application.emi,
    interestRate: distribution.interestRateForCustomer,
    tenureMonths: distribution.tenure ?? application.monthsToRepay,
    bankType: partnerDetails.bankCategory ?? application.bankType ?? "N/A",
    distributionId: distribution._id,
    timestamp: new Date().toISOString(),
  };

  // Utility: safely convert to number to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "-";
    const num = Number(amount);
    if (!Number.isFinite(num)) return "-";
    return `₹${new Intl.NumberFormat("en-IN").format(num)}`;
  };

  // --- Approval detection logic (robust) ---
  const banksApproval = Array.isArray(application?.BanksApprovalDetails)
    ? application.BanksApprovalDetails
    : [];

  // Find any approval record that marks a bank as accepted (global accepted record)
  const acceptedApproval = banksApproval.find((a) => !!a.customerAccepted) || null;

  // Helper: find the approval entry that corresponds specifically to this distribution
  const matchingApproval = banksApproval.find((approval) => {
    if (!approval) return false;
    // 1) Match by distributionId if present
    if (approval.distributionId && distribution._id && approval.distributionId === distribution._id)
      return true;
    // 2) Match by approval._id === distribution._id (sometimes stored differently)
    if (approval._id && distribution._id && String(approval._id) === String(distribution._id))
      return true;
    // 3) Match by bankName (case-insensitive) as a fallback
    if (approval.bankName && bankName && approval.bankName.toLowerCase() === bankName.toLowerCase())
      return true;
    return false;
  });

  // Determine if this bank was accepted by the customer
  const isThisAccepted = (() => {
    // If we have a specific matching approval record, trust it
    if (matchingApproval) return !!matchingApproval.customerAccepted;

    // If there is a global acceptedApproval, match by distributionId/_id or bankName
    if (acceptedApproval) {
      const acceptedDistId = acceptedApproval.distributionId ?? acceptedApproval._id ?? null;
      if (acceptedDistId && distribution._id && String(acceptedDistId) === String(distribution._id))
        return true;
      if (
        acceptedApproval.bankName &&
        bankName &&
        acceptedApproval.bankName.toLowerCase() === bankName.toLowerCase()
      )
        return true;
    }

    // Fallback: sometimes distribution has a direct flag (last resort)
    if (distribution.customerApproval || distribution.customerAccepted) return true;

    return false;
  })();

  // Determine if this bank was explicitly rejected by the customer
  const isThisRejected = matchingApproval
    ? !!matchingApproval.customerRejected
    : !!distribution.customerRejected;

  // Whether some other bank has been accepted (so this one is not allowed to be approved)
  const otherOfferAccepted = !!acceptedApproval && !isThisAccepted;

  // For rendering textual status in other parts of UI
  const customerStatus = {
    accepted: isThisAccepted,
    rejected: isThisRejected,
    rejectedAt: matchingApproval?.customerRejectedAt ?? null,
  };

  // --- Actions ---
  const sendToCustomer = async () => {
    if (!applicationId) {
      toast.error("Loan application ID is missing.");
      return;
    }
    try {
      const response = await creditAPI.sendLoanDetailsToCustomer(applicationId, dataToCustomer);
      if (response?.success && response?.data) {
        toast.success("Details sent to customer and saved!");
        if (typeof onDetailsSentSuccessfully === "function") onDetailsSentSuccessfully();
      } else {
        toast.error(response?.message || "Failed to send details to customer.");
      }
    } catch (err) {
      console.error("Error sending loan details to customer:", err);
      toast.error("Failed to send loan details to customer.");
    }
  };

  const handleApproveClick = () => {
    if (distribution && distribution._id) {
      openConfirmDialog(distribution._id);
    }
  };

  return (
    <div className="bg-black rounded-lg p-6 mb-4">
      <div className="flex justify-between items-start">
        <div className="flex items-center justify-start gap-3">
          <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
            {bankLogo ? (
              <img src={bankLogo} alt={`${bankName} Logo`} className="w-full h-full object-contain" />
            ) : (
              <span className="text-2xl font-bold text-black">{bankLogoChar}</span>
            )}
          </div>
          <h3 className="text-white font-medium">{bankName}</h3>
        </div>

        <div className="flex items-center gap-3">
          {/* Bank rejected by bank */}
          {distribution.status === "rejected" && (
            <div 
              className="p-2 bg-red-900/20 border border-red-500/30 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleRejectionClick}
              title="Click to view rejection reason"
            >
              <p className="text-red-400 text-xs font-medium">🏦 Bank rejected</p>
            </div>
          )}

          {/* Customer rejected specifically for this bank */}
          {customerStatus.rejected && (
            <div className="p-2 bg-red-900/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-xs font-medium">🙅 Customer rejected</p>
            </div>
          )}

          {/* Show accepted badge ONLY for the actually accepted bank */}
          {customerStatus.accepted && (
            <div className="p-2 bg-green-900/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-xs font-medium">✅ Customer accepted</p>
            </div>
          )}

          {/* If some other bank was accepted, show 'Other offer accepted' for non-accepted banks */}
          {otherOfferAccepted && !customerStatus.accepted && (
            <div className="p-2 bg-orange-900/20 border border-orange-500/30 rounded-lg">
              <p className="text-orange-400 text-xs font-medium">🔒 Other offer accepted</p>
            </div>
          )}

          <button
            onClick={sendToCustomer}
            disabled={
              customerStatus.accepted === true ||
              customerStatus.rejected === true ||
              distribution.status !== "approved" ||
              otherOfferAccepted
            }
            className={`flex flex-row items-center gap-2 py-2 px-4 rounded-lg text-base font-bold min-w-[180px] ${
              customerStatus.accepted === true ||
              customerStatus.rejected === true ||
              distribution.status !== "approved" ||
              otherOfferAccepted
                ? "bg-white text-black cursor-not-allowed opacity-100"
                : "bg-[#ffca20] text-black"
            }`}
          >
            <Forward size={20} />
            <span>Send loan details to customers</span>
          </button>
        </div>
      </div>

      {/* Bank Details - Always visible */}
      <div className="mt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-2">
          <p className="text-sm text-gray-400 flex flex-col">
            Bank Type:{" "}
            <span className="text-white font-medium">{partnerDetails.bankCategory ?? "N/A"}</span>
          </p>

          <p className="text-sm text-gray-400 flex flex-col">
            IFSC Code: <span className="text-white font-medium">{partnerDetails.branchIFSCCode ?? "N/A"}</span>
          </p>

          <p className="text-sm text-gray-400 flex flex-col">
            Branch Name: <span className="text-white font-medium">{partnerDetails.branchName ?? "N/A"}</span>
          </p>

          <p className="text-sm text-gray-400 flex flex-col">
            Bank Address:{" "}
            <span className="text-white font-medium">{partnerDetails.bankAddress ?? "N/A"}</span>
          </p>
        </div>
      </div>

      {distribution.status === "approved" ? (
        <div className="flex flex-col lg:flex-row">
          <div className="flex-1 lg:border-r border-[#333333] lg:pr-2">
            <div className="flex gap-4 w-full mt-2">
              <div className="flex-1 flex-col rounded-md p-2 border border-[#333333]">
                <p className="text-[#F3F3F3] text-xs mb-1">Approved Amount</p>
                <p className="text-[#FFCA20] font-semibold text-lg my-0">
                  {formatCurrency(distribution.approveAmount ?? application.approvedAmount ?? application.principle)}
                </p>
              </div>

              <div className="flex-1 flex-col rounded-md p-2 border border-[#333333]">
                <p className="text-[#F3F3F3] text-xs mb-1">Monthly EMI</p>
                <p className="text-[#FFCA20] font-semibold text-lg my-0">
                  {formatCurrency(distribution.emi ?? application.monthlyEMI ?? application.emi)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-x-6 gap-y-4 pt-5 mt-auto">
              <div>
                <p className="text-gray-400 text-sm mb-1">Loan Type</p>
                <p className="text-white text-base font-medium">{application.loanType ?? "-"}</p>
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-1">Interest Rate</p>
                <p className="text-white text-base font-medium">
                  {distribution.interestRateForCustomer ? `${distribution.interestRateForCustomer}% p.a` : "-"}
                </p>
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-1">Tenure Requested</p>
                <p className="text-white text-base font-medium">
                  {distribution.tenure ?? (application.monthsToRepay ? `${application.monthsToRepay} months` : "-")}
                </p>
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-1">Processing Fee</p>
                <p className="text-white text-base font-medium">
                  {formatCurrency(distribution.processingFee ?? application.processingFee)}
                </p>
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-1">Insurance Fees</p>
                <p className="text-white text-base font-medium">
                  {formatCurrency(distribution.insuranceFees ?? application.insuranceFee)}
                </p>
              </div>

              <div>
                <p className="text-gray-400 text-sm mb-1">Platform Fees</p>
                <p className="text-white text-base font-medium">
                  {formatCurrency(distribution.platformFee ?? application.platformFee)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 lg:border-l border-[#333333] lg:pl-2 mt-4 lg:mt-0">
            <div className="flex justify-end mt-4 lg:mt-32 relative">
              {!otherOfferAccepted && !customerStatus.rejected && distribution.status !== "rejected" && (
                <button
                  onClick={handleApproveClick}
                  disabled={isSubmitting || !distribution._id || !customerStatus.accepted}
                  className={`bg-green-600 text-white py-3 px-3 lg:px-6 rounded-lg border-none text-sm lg:text-base font-bold flex items-center justify-center min-w-[140px] lg:min-w-[180px]
                    ${
                      !customerStatus.accepted
                        ? "bg-gray-400 text-white cursor-not-allowed opacity-70"
                        : isSubmitting || !distribution._id
                        ? "bg-green-600 text-white cursor-not-allowed opacity-70"
                        : "bg-green-600 text-white cursor-pointer hover:bg-green-700"
                    }`}
                >
                  {isSubmitting ? "Selecting..." : "Approve this Bank ✓"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : distribution.status === "rejected" ? (
        <div className="flex justify-end relative">
          <button
            className="bg-red-600 text-white py-3 px-6 rounded-lg border-none text-base font-bold cursor-not-allowed opacity-70 flex items-center justify-center min-w-[180px]"
            disabled
          >
            Rejected Application
          </button>
        </div>
      ) : (
        <div className="flex justify-end relative p-2 text-[#FFCA20]">Waiting for bank's response</div>
      )}

      {/* Rejection Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-[#131313] rounded-lg p-6 w-96 max-w-[90vw] border border-[#333333]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">Rejection Reason</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-white text-sm">
                {selectedReason || "No rejection reason provided."}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleSendForApproval}
                disabled={isSendingForApproval}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSendingForApproval ? "Sending..." : "Send for Bank Approval"}
              </button>
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-[#FFCA20] text-black rounded-md hover:bg-yellow-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankCard;
